import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Marker } from 'react-leaflet'
import { LatLngTuple } from 'leaflet'
import { Floor } from 'models/Building'
import { useRecoilValue } from 'recoil'
import { listPointState } from 'store/buildingStore'
import useSWR from 'swr'
import { FloorDetail } from 'types/building'

import Button from 'components/atoms/Button'
import Spinner from 'components/atoms/Spinner'
import OpenMiniMapIcon from 'components/icons/OpenMiniMapIcon'
import TourViewerOpenIcon from 'components/icons/TourViewerOpenIcon'

import FloorMapView from './FloorMapView'
import { PointStyle } from './PointStyle'
import SelectFloor from './SelectFloor'
import ZoomControl from './ZoomControl'

import useFloorMap from '../../hook/Leflet/useFloorMap'
import useRadarPointMap from '../../hook/Leflet/useRadarPointMap'
import { SelectOption } from '../type'

export default function TourViewerFloorMap({
  floors,
  setSelectedPointID
}: {
  floors: Floor[] | undefined
  setSelectedPointID: Dispatch<SetStateAction<string>>
}) {
  const points = useRecoilValue(listPointState)
  const [expandedMap, setExpandedMap] = useState<boolean>(true)
  const [isMediumScreen, setIsMediumScreen] = useState(false)
  const [rotationAngle, setRotationAngle] = useState(0)
  const [radarReady, setRadarReady] = useState(false)
  // Use the floor map hook for map management
  const {
    mapContainerRef,
    isZoomOutMap,
    isMapLoading,
    showMap,
    imgBounds,
    getMapSizeInlineStyle,
    handleZoomToggle,
    handleZoomIn,
    handleZoomOut,
    handleZoomToPoint,
    resetMap,
    getFloorSelectInlineStyle,
    handleSelectFloor,
    currentViewer,
    handleMarkerClick,
    animateMapChange,
    sync360ViewToArc,
    povMarkerRef,
    floorDetail,
    setFloorDetail,
    zoomValue,
    setCurrentViewer
  } = useFloorMap({
    initialZoomOut: false,
    setSelectedPointID
  })

  // Use the radar point map hook
  const {
    getRotatedMarkerIcon,
    mapEventHandler,
    radarEventHandler,
    radarMaker: radarMakerData
  } = useRadarPointMap({
    points,
    currentViewer,
    isZoomOutMap,
    handleMarkerClick,
    povMarkerRef
  })

  // Fetch floor detail
  const { isValidating: isValidatingFloor } = useSWR<FloorDetail>(
    currentViewer?.selectedFloor
      ? {
          url: `v1/floors/${currentViewer?.selectedFloor}`
        }
      : null,
    {
      onSuccess: (data) => {
        setFloorDetail(data)
        resetMap() // Reset map when floor changes
      }
    }
  )

  // Prepare floor options for dropdown
  const floorOptions: SelectOption[] =
    floors?.map((floor) => ({
      value: floor.id,
      label: (
        <span className="text-[#fff] text-[13px] font-bold">{floor.name}</span>
      )
    })) || []

  // Enhanced point style
  const getEnhancedPointStyle = useCallback(
    (point: any, selectedPointId: string | undefined, isZoomed: boolean) => {
      return PointStyle(point, selectedPointId, isZoomed)
    },
    []
  )

  // Track click state
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [clickCount, setClickCount] = useState(0)

  const isDraggingRef = useRef(false)
  const mouseDownPositionRef = useRef<{ x: number; y: number } | null>(null)

  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownPositionRef.current = { x: e.clientX, y: e.clientY }
    isDraggingRef.current = false
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (mouseDownPositionRef.current) {
      const deltaX = Math.abs(e.clientX - mouseDownPositionRef.current.x)
      const deltaY = Math.abs(e.clientY - mouseDownPositionRef.current.y)

      if (deltaX > 5 || deltaY > 5) {
        isDraggingRef.current = true
      }
    }
  }

  const handleMouseUp = () => {
    mouseDownPositionRef.current = null
  }

  const handleClick = (e: React.MouseEvent) => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false
      return
    }
    e.preventDefault()
    setClickCount((prev) => prev + 1)

    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current)
    }

    clickTimeoutRef.current = setTimeout(() => {
      if (clickCount === 0) {
        handleZoomToggle()
      }
      setClickCount(0)
    }, 300)
  }

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current)
    }
    setClickCount(0)
  }

  // Clean up timeout on component unmount
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current)
      }
    }
  }, [])

  // Check if screen is medium or larger
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMediumScreen(window.innerWidth >= 768)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)

    return () => {
      window.removeEventListener('resize', checkScreenSize)
    }
  }, [])

  // Generate radar marker component
  const radarMaker = useMemo(() => {
    if (!radarMakerData || !radarReady) return null

    const position: LatLngTuple = [
      radarMakerData?.position[0],
      radarMakerData?.position[1]
    ]

    const radarRotate = currentViewer.isSelectPoint
      ? radarMakerData?.rotate
      : rotationAngle

    return (
      <Marker
        position={position}
        ref={povMarkerRef}
        zIndexOffset={radarMakerData?.zIndexOffset}
        icon={getRotatedMarkerIcon(radarRotate, isZoomOutMap)}
        draggable={false}
        eventHandlers={radarEventHandler()}
      />
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    radarReady,
    radarMakerData,
    povMarkerRef,
    isZoomOutMap,
    getRotatedMarkerIcon,
    radarEventHandler
  ])

  sync360ViewToArc((rotationAngle: number) => {
    setRotationAngle(rotationAngle)
    if (currentViewer.isSelectPoint) {
      setCurrentViewer({ ...currentViewer, isSelectPoint: false })
    }
    return getRotatedMarkerIcon(rotationAngle, isZoomOutMap)
  })

  useEffect(() => {
    if (currentViewer?.selectedPoint) {
      setRadarReady(false)
      const timeout = setTimeout(() => {
        setRadarReady(true)
      }, 1000)
      return () => clearTimeout(timeout)
    }
    setRadarReady(false)
  }, [currentViewer?.selectedPoint])

  return (
    <div className="absolute top-0 right-0 z-[99] tour-view-map-mini">
      <div className="flex justify-end">
        <div className="flex">
          <Button
            sizing="w-[19px] h-[40px]"
            className="bg-[#074947] flex justify-center items-center !text-white !rounded-none"
            onClick={() => {
              handleZoomToggle()
            }}
          >
            <TourViewerOpenIcon
              className={`transition-all duration-300 ease-in-out transform ${
                !isZoomOutMap ? 'rotate-0' : '-rotate-180'
              }`}
            />
          </Button>

          <SelectFloor
            style={getFloorSelectInlineStyle()}
            options={floorOptions}
            value={currentViewer?.selectedFloor}
            onSelect={handleSelectFloor}
          />
        </div>
        <Button
          sizing="w-[19px] h-[40px]"
          className="bg-[#074947]/50 backdrop-blur-xl flex justify-center items-center !rounded-none"
          onClick={() => {
            if (mapContainerRef?.current) {
              mapContainerRef?.current?.invalidateSize({ animate: false })
            }

            if (isZoomOutMap && expandedMap) {
              handleZoomToggle()
            }

            animateMapChange(() => {
              setExpandedMap(!expandedMap)
            })
          }}
        >
          <OpenMiniMapIcon />
        </Button>
      </div>
      {/* Map area */}
      {floorDetail?.mapFile?.s3Path && (
        <div
          className={`relative grid grid-col w-full transition-all duration-500 ease-in-out ${
            expandedMap
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-2 pointer-events-none'
          }`}
        >
          <Spinner spinning={isValidatingFloor || isMapLoading}>
            <div className="flex flex-col items-end">
              <div
                className={`relative bg-white cursor-pointer transition-all duration-500 ease-in-out ${
                  showMap ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                } `}
                style={{
                  ...getMapSizeInlineStyle()
                }}
                onClick={handleClick}
                onDoubleClick={handleDoubleClick}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
              >
                <FloorMapView
                  mapContainerRef={mapContainerRef}
                  floorDetail={floorDetail}
                  points={points}
                  imgBounds={imgBounds}
                  currentViewer={currentViewer}
                  isZoomOutMap={isZoomOutMap}
                  isMediumScreen={isMediumScreen}
                  handleMarkerClick={handleMarkerClick}
                  radarMaker={radarMaker}
                  getEnhancedPointStyle={getEnhancedPointStyle}
                  mapEventHandler={mapEventHandler}
                  showNumbers={false}
                />

                {/* Loading overlay */}
                {isMapLoading && (
                  <div className="absolute inset-0 flex items-center justify-center z-10 bg-white bg-opacity-50 transition-opacity duration-500">
                    <div className="animate-pulse flex flex-col items-center">
                      <div
                        className={`${
                          isZoomOutMap ? 'w-12 h-12' : 'w-2 h-2'
                        } border-4 border-t-4 border-gray-200 rounded-full animate-spin mb-2 transition-all duration-500`}
                        style={{ borderTopColor: '#5F9EA0' }}
                      />
                      <div className="text-sm text-gray-600 transition-opacity duration-500">
                        読み込み中...
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div
                className={`transition-all duration-500 ease-in-out ${
                  expandedMap
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-2'
                }`}
              >
                <ZoomControl
                  isZoomOutMap={isZoomOutMap}
                  zoomIn={handleZoomIn}
                  zoomOut={handleZoomOut}
                  zoomPoint={() => {
                    if (currentViewer.selectedPoint) {
                      const point = points.find(
                        (p) => p.id === currentViewer.selectedPoint
                      )
                      if (point) {
                        handleZoomToPoint(point.x, point.y)
                      }
                    }
                  }}
                  zoomValue={zoomValue}
                />
              </div>
            </div>
          </Spinner>
        </div>
      )}
    </div>
  )
}
